import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useContext,
  useMemo
} from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { featureCollection } from '@turf/helpers';
import { isUndefined, isEmpty, isEqual } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { Context } from 'components/Store';
import useTwoWayState from 'hooks/useTwoWayState';
import {
  DragCircleMode,
  DirectSelectMode as DirectMode,
  SimpleSelectMode
} from './CircleModes';
import SelectOnlyMode from './SelectOnlyMode';
import PropertyMap from './PropertyMap';
import useMapData from '../../../hooks/useMapData';
import { featuresMatch } from '../../../helpers/propertyDataHelpers';
import PropertyMapContext from '../PropertyMapContext';
import { MapEventContext } from './MapEventContext';
import {
  getAllFeatures,
  getGeoJSONCollectionFeatures,
  getFeatureWithParentId,
  getSyncFeaturesForUpdate
} from '../../../helpers/mapApiHelpers';
import { LAND_TYPES, DEFAULT_ZOOM } from '../../../helpers/constants';
import MapEventDisplay from './MapEventDisplay';
import WithExportPreview from './WithExportPreview';

export const handleDrawCreate = (
  featuresToSave,
  featureIdsToDelete,
  setFeatures,
  parentId
) => {
  for (let i = 0; i < featuresToSave.length; i += 1) {
    // eslint-disable-next-line no-param-reassign
    if (!featuresToSave[i].id) featuresToSave[i].id = uuidv4();
  }
  // if a 2nd arg is provided, that is for features to be removed
  const filterPredicate = featureIdsToDelete
    ? ({ id }) => !featureIdsToDelete.includes(id)
    : () => true;
  setFeatures(previousFeatures => {
    const updatedFeatures = featuresToSave
      .filter(feature => feature.geometry?.coordinates)
      .map(item => getFeatureWithParentId(item, { _id: parentId }));
    return previousFeatures.filter(filterPredicate).concat(updatedFeatures);
  });
};

// Only the geometry is written back. The draw control reports every selected
// shape on any mouse-up that involved movement, a shift-click included, so
// this list is not the selection; the screens derive what they save from the
// selection and these features.
export const handleDrawUpdate = (
  shapeId,
  changedFeatures,
  setFeatures,
  syncedFeatures
) => {
  setFeatures(previousFeatures =>
    previousFeatures.map(featureItem => {
      const changedFeature = changedFeatures.find(
        item => item.id === featureItem.id
      );
      return {
        ...featureItem,
        geometry: changedFeature
          ? changedFeature.geometry
          : featureItem.geometry
      };
    })
  );
  // eslint-disable-next-line no-param-reassign
  syncedFeatures.current = getSyncFeaturesForUpdate(
    syncedFeatures.current,
    shapeId
  );
};

export const handleDrawDelete = (deletedIds, setFeatures) => {
  setFeatures(previousFeatures =>
    previousFeatures.filter(({ id }) => !deletedIds.includes(id))
  );
};

const propertyMapWrapperDefaultPropFields = [];
const propertyMapWrapperDefaultPropOnClickShape = () => {};
const propertyMapWrapperDefaultPropOnFeaturesChange = () => {};
const propertyMapWrapperDefaultPropSetCurrentCoordinates = () => {};
const propertyMapWrapperDefaultPropOnFit = () => {};
const propertyMapWrapperDefaultPropSetZoom = () => {};
const PropertyMapWrapper = ({
  currentCoordinates = /** @type {object|null} */ (null),
  displayOnly = false,
  fields = propertyMapWrapperDefaultPropFields,
  geoJSONCollection = /** @type {object|null} */ (null),
  liveUpdate = true,
  onClickShape = propertyMapWrapperDefaultPropOnClickShape,
  onFeaturesChange = propertyMapWrapperDefaultPropOnFeaturesChange,
  onSelectFeatures = null,
  parentId = null,
  selectedFeatureIds = undefined,
  setCurrentCoordinates = /** @type {(...args: any[]) => any} */ (propertyMapWrapperDefaultPropSetCurrentCoordinates),
  setZoom = /** @type {(...args: any[]) => any} */ (propertyMapWrapperDefaultPropSetZoom),
  stretch = false,
  onFit = propertyMapWrapperDefaultPropOnFit,
  zoom = DEFAULT_ZOOM,
  fieldsLoading = false,
  labelName = '',
  shapeIdSelected = null,
  isCreate = false,
  customStyles = /** @type {Record<string, {fillColor?: string, fillOutlineColor?: string}>|boolean} */ (false),
  showArchivedLabels = false,
  disableAutoFit = false
}) => {
  const {
    selectedShapeIds,
    setSelectedShapeIds,
    setSelectedShapesExternally,
    internalShapeSelectionChanges,
    mapChangeEvent,
    newFeatures,
    addNewMapFeature,
    addNewMapFeatures,
    archiveMapHistoryFeature
  } = useMapData();

  const [markerOn, setMarkerOn] = useState(false);
  const [{ selectedProperty, fieldsToMassiveAssign }] = useContext(Context);
  const modes = {
    ...MapboxDraw.modes,
    direct_select: DirectMode,
    simple_select: SimpleSelectMode,
    drag_circle: DragCircleMode,
    select_only: SelectOnlyMode
  };

  const defaultMode =
    onSelectFeatures && displayOnly ? 'select_only' : 'simple_select';
  const comparator = useCallback(
    (previous, next) =>
      featuresMatch(previous, next) &&
      isEqual(
        previous?.map(feature => feature.properties),
        next?.map(feature => feature.properties)
      ),
    []
  );

  const [
    features,
    setFeatures,
    externalSetFeatures,
    internalFeaturesChanges
  ] = useTwoWayState([], comparator);

  const { location } = useHistory();

  const [mode, setMode] = useState(defaultMode);
  const [viewContext] = useState(LAND_TYPES.FIELD);
  const syncedFeatures = useRef([]);

  // Live update allows changes to be made to api in real time
  useEffect(() => {
    // get featureCollection from geoJSONCollection of from fields
    let changedFeatures = [];
    changedFeatures = geoJSONCollection?.features?.length
      ? getGeoJSONCollectionFeatures(geoJSONCollection)
      : getAllFeatures(fields);
    if (!isUndefined(changedFeatures)) {
      externalSetFeatures(changedFeatures);
    }
  }, [fields, geoJSONCollection, externalSetFeatures]);

  // pick up changes to features state, trigger onFeaturesChange, and track synced features
  useEffect(() => {
    if (!isUndefined(internalFeaturesChanges)) {
      onFeaturesChange(internalFeaturesChanges);
    }
  }, [internalFeaturesChanges, onFeaturesChange]);

  // changes to selectedFeatureIds prop are external, update accordingly
  useEffect(() => {
    if (!isUndefined(selectedFeatureIds)) {
      setSelectedShapesExternally(selectedFeatureIds);
    }
  }, [selectedFeatureIds, setSelectedShapesExternally]);

  // notify the parent that selection changes were triggered from the map
  useEffect(() => {
    if (onSelectFeatures && !isUndefined(internalShapeSelectionChanges)) {
      onSelectFeatures(internalShapeSelectionChanges);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalShapeSelectionChanges]);

  useEffect(() => {
    if (!Array.isArray(shapeIdSelected)) {
      setSelectedShapeIds([shapeIdSelected]);
    } else {
      setSelectedShapeIds(shapeIdSelected);
    }
  }, [shapeIdSelected, setSelectedShapeIds]);

  const modeReset = useCallback(() => {
    setMode(defaultMode);
  }, [defaultMode]);

  const collection = useMemo(() => featureCollection(features), [features]);

  // Keyed on whether something is selected, not on the selection object: the
  // object changes on every click, and a new styles object makes the layers
  // rebuild and reload their sources before the new data has even arrived.
  const hasSelection =
    !isEmpty(selectedProperty) || fieldsToMassiveAssign.length > 0;
  const mergedCustomStyles = useMemo(
    () =>
      hasSelection || customStyles
        ? {
            // A line layer rather than the fill's hairline outline: on shared
            // edges the neighbours' white border covered the hairline, and
            // the Select Properties drawer draws its selection at this width.
            focused: {
              fillColor: 'rgba(49, 180, 242, 0.4)',
              fillOutlineColor: 'rgba(49, 180, 242, 1)',
              lineWidth: 2.5
            },
            ...customStyles
          }
        : null,
    [hasSelection, customStyles]
  );

  const mapCtxValue = useMemo(
    () => ({
      addNewMapFeature,
      addNewMapFeatures,
      archiveMapHistoryFeature,
      onClickShape,
      fields,
      mapChangeEvent,
      markerOn,
      newFeatures,
      selectedShapeIds,
      setMarkerOn,
      setSelectedShapeIds
    }),
    [
      addNewMapFeature,
      addNewMapFeatures,
      archiveMapHistoryFeature,
      onClickShape,
      fields,
      mapChangeEvent,
      markerOn,
      newFeatures,
      selectedShapeIds,
      setMarkerOn,
      setSelectedShapeIds
    ]
  );

  const onDrawCreate = useCallback(
    (featuresToSave, featureIdsToDelete) =>
      handleDrawCreate(
        featuresToSave,
        featureIdsToDelete,
        setFeatures,
        parentId
      ),
    [setFeatures, parentId]
  );

  const onDrawUpdate = useCallback(
    (shapeId, changedFeatures) =>
      handleDrawUpdate(shapeId, changedFeatures, setFeatures, syncedFeatures),
    [setFeatures]
  );

  const onDrawDelete = useCallback(
    deletedIds => handleDrawDelete(deletedIds, setFeatures),
    [setFeatures]
  );

  const resolvedLabelName =
    location?.label === null || location?.label === undefined
      ? labelName
      : location?.label;

  return (
    <MapEventContext>
      <PropertyMapContext.Provider value={mapCtxValue}>
        <WithExportPreview fields={fieldsToMassiveAssign}>
          <PropertyMap
            currentCoordinates={currentCoordinates}
            setCurrentCoordinates={setCurrentCoordinates}
            displayOnly={displayOnly}
            onFit={onFit}
            geoJSONCollection={collection}
            customStyles={mergedCustomStyles}
            handleDrawCreate={onDrawCreate}
            handleDrawUpdate={onDrawUpdate}
            handleDrawDelete={onDrawDelete}
            markerOn={markerOn}
            mode={mode}
            modeReset={modeReset}
            setMode={setMode}
            modes={modes}
            liveUpdate={liveUpdate}
            viewContext={viewContext}
            zoom={zoom}
            setZoom={setZoom}
            stretch={stretch}
            fieldsLoading={fieldsLoading}
            labelName={resolvedLabelName}
            isCreate={isCreate}
            showArchivedLabels={showArchivedLabels}
            disableAutoFit={disableAutoFit}
          />
        </WithExportPreview>
      </PropertyMapContext.Provider>
      <MapEventDisplay />
    </MapEventContext>
  );
};

PropertyMapWrapper.propTypes = {
  currentCoordinates: PropTypes.shape({
    coordinates: PropTypes.arrayOf(PropTypes.number),
    userGeolocation: PropTypes.arrayOf(PropTypes.number)
  }),
  customStyles: PropTypes.objectOf(
    PropTypes.shape({
      fillColor: PropTypes.string,
      fillOutlineColor: PropTypes.string
    })
  ),
  displayOnly: PropTypes.bool,
  fields: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-next-line react/forbid-prop-types
  geoJSONCollection: PropTypes.object,
  liveUpdate: PropTypes.bool,
  onClickShape: PropTypes.func,
  onFeaturesChange: PropTypes.func,
  onSelectFeatures: PropTypes.func,
  parentId: PropTypes.string,
  selectedFeatureIds: PropTypes.arrayOf(PropTypes.string),
  setCurrentCoordinates: PropTypes.func,
  setZoom: PropTypes.func,
  stretch: PropTypes.bool,
  onFit: PropTypes.func,
  zoom: PropTypes.arrayOf(PropTypes.number),
  fieldsLoading: PropTypes.bool,
  labelName: PropTypes.string,
  shapeIdSelected: PropTypes.string,
  isCreate: PropTypes.bool,
  showArchivedLabels: PropTypes.bool,
  disableAutoFit: PropTypes.bool
};

export default PropertyMapWrapper;
